import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  Sequence,
  Img,
  Video,
  Audio,
  spring,
} from 'remotion';

// 图片URL
const IMAGES = {
  product1: 'https://images.pexels.com/photos/2085120/pexels-photo-2085120.jpeg?auto=compress&cs=tinysrgb&w=800',
  product2: 'https://images.pexels.com/photos/1510392/pexels-photo-1510392.jpeg?auto=compress&cs=tinysrgb&w=800',
  orchard: 'https://images.pexels.com/photos/2629728/pexels-photo-2629728.jpeg?auto=compress&cs=tinysrgb&w=800',
  tree: 'https://images.pexels.com/photos/2165759/pexels-photo-2165759.jpeg?auto=compress&cs=tinysrgb&w=800',
};

// 毛刺效果文字组件
const GlitchText: React.FC<{
  children: React.ReactNode;
  frame: number;
  delay?: number;
  style?: React.CSSProperties;
}> = ({ children, frame, delay = 0, style }) => {
  const currentFrame = frame - delay;
  
  const glitchAmount = interpolate(
    currentFrame,
    [0, 10, 12, 14, 16, 20],
    [0, 1, 0.5, 1.5, 0.8, 0],
    { extrapolateRight: 'clamp' }
  );

  const translateX1 = interpolate(glitchAmount, [0, 1], [0, Math.random() * 10 - 5]);
  const translateX2 = interpolate(glitchAmount, [0, 1], [0, Math.random() * 10 - 5]);
  const hueRotate = interpolate(glitchAmount, [0, 1], [0, Math.random() * 360]);

  const clipPath1 = interpolate(glitchAmount, [0, 1], [0, Math.random() * 50 + 20]);
  const clipPath2 = interpolate(glitchAmount, [0, 1], [100, Math.random() * 50]);

  const opacity = interpolate(currentFrame, [0, 5], [0, 1], { extrapolateRight: 'clamp' });

  return (
    <h2
      style={{
        fontFamily: 'Arial Black, sans-serif',
        fontSize: '4.5rem',
        color: 'white',
        textAlign: 'center',
        position: 'relative',
        opacity,
        ...style,
      }}
    >
      <span
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: '100%',
          height: '100%',
          clipPath: `inset(0% ${clipPath1}% 0% 0%)`,
          filter: `hue-rotate(${hueRotate}deg)`,
          transform: `translateX(${translateX1}px)`,
          zIndex: 1,
        }}
      >
        {children}
      </span>
      <span
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: '100%',
          height: '100%',
          clipPath: `inset(0% 0% 0% ${clipPath2}%)`,
          filter: `hue-rotate(${-hueRotate}deg)`,
          transform: `translateX(${translateX2}px)`,
          zIndex: 2,
        }}
      >
        {children}
      </span>
      {children}
    </h2>
  );
};

// 撕纸边缘组件
const TornPaperOverlay: React.FC<{
  frame: number;
  color: string;
}> = ({ frame, color }) => {
  const topTranslate = interpolate(frame, [0, 10], [-100, 0], {
    extrapolateRight: 'clamp',
  });
  const bottomTranslate = interpolate(frame, [0, 10], [100, 0], {
    extrapolateRight: 'clamp',
  });
  const opacity = interpolate(frame, [0, 5], [0, 1], {
    extrapolateRight: 'clamp',
  });

  return (
    <>
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '25%',
          backgroundColor: color,
          clipPath: 'polygon(0 0, 100% 0, 100% 60%, 95% 80%, 90% 70%, 85% 90%, 80% 65%, 75% 85%, 70% 70%, 65% 95%, 60% 75%, 55% 85%, 50% 70%, 45% 90%, 40% 75%, 35% 85%, 30% 70%, 25% 95%, 20% 75%, 15% 85%, 10% 70%, 5% 90%, 0 60%)',
          transform: `translateY(${topTranslate}%)`,
          opacity,
          zIndex: 10,
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '25%',
          backgroundColor: color,
          clipPath: 'polygon(0 100%, 100% 100%, 100% 40%, 95% 20%, 90% 30%, 85% 10%, 80% 35%, 75% 15%, 70% 30%, 65% 5%, 60% 25%, 55% 15%, 50% 30%, 45% 10%, 40% 25%, 35% 15%, 30% 30%, 25% 5%, 20% 25%, 15% 15%, 10% 30%, 5% 10%, 0 40%)',
          transform: `translateY(${bottomTranslate}%)`,
          opacity,
          zIndex: 10,
        }}
      />
    </>
  );
};

// 描边文字组件
const OutlineText: React.FC<{
  children: React.ReactNode;
  frame: number;
  delay?: number;
  style?: React.CSSProperties;
}> = ({ children, frame, delay = 0, style }) => {
  const currentFrame = frame - delay;
  const opacity = interpolate(currentFrame, [0, 10], [0, 1], { extrapolateRight: 'clamp' });
  const translateY = interpolate(currentFrame, [0, 10], [20, 0], { extrapolateRight: 'clamp' });

  return (
    <h1
      style={{
        fontFamily: 'Arial Black, sans-serif',
        fontSize: '4rem',
        color: 'transparent',
        WebkitTextStroke: '3px white',
        textAlign: 'center',
        opacity,
        transform: `translateY(${translateY}px)`,
        ...style,
      }}
    >
      {children}
    </h1>
  );
};

// 基础动画文字组件
const AnimatedText: React.FC<{
  children: React.ReactNode;
  frame: number;
  delay?: number;
  style?: React.CSSProperties;
}> = ({ children, frame, delay = 0, style }) => {
  const currentFrame = frame - delay;
  const opacity = interpolate(currentFrame, [0, 10], [0, 1], { extrapolateRight: 'clamp' });
  const scale = spring({
    frame: currentFrame,
    from: 0.8,
    to: 1,
    fps: 30,
    config: { damping: 10, stiffness: 100 },
  });

  return (
    <h1
      style={{
        fontFamily: 'Arial Black, sans-serif',
        fontSize: '4rem',
        color: 'white',
        textAlign: 'center',
        opacity,
        transform: `scale(${scale})`,
        ...style,
      }}
    >
      {children}
    </h1>
  );
};

// 场景1: CYBER SALE
const Scene1: React.FC = () => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();

  const scale = interpolate(frame, [0, 15], [1.0, 1.15], { extrapolateRight: 'clamp' });
  const textOpacity = interpolate(frame, [0, 5], [0, 1], { extrapolateRight: 'clamp' });
  const textScale = spring({
    frame: frame,
    from: 0.8,
    to: 1,
    fps: 30,
    config: { damping: 10, stiffness: 100 },
  });

  return (
    <AbsoluteFill>
      <div style={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
      }}>
        <Img
          src={IMAGES.product1}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transform: `scale(${scale})`,
            filter: 'brightness(0.7) saturate(1.2)',
          }}
        />
      </div>
      <AbsoluteFill style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}>
        <h1 style={{
          fontFamily: 'Arial Black, sans-serif',
          fontSize: 140,
          fontWeight: 900,
          color: '#ffffff',
          textShadow: '8px 8px 0px rgba(0, 0, 0, 0.5)',
          transform: `scale(${textScale})`,
          opacity: textOpacity,
          letterSpacing: 10,
        }}>
          CYBER SALE
        </h1>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// 场景2: UP TO 30% OFF
const Scene2: React.FC = () => {
  const frame = useCurrentFrame();

  const upToOpacity = interpolate(frame, [0, 10], [0, 1], { extrapolateRight: 'clamp' });
  const percentScale = spring({
    frame: frame - 10,
    from: 0.5,
    to: 1,
    fps: 30,
    config: { damping: 10, stiffness: 100 },
  });
  const percentOpacity = interpolate(frame, [10, 15], [0, 1], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill style={{ backgroundColor: 'black' }}>
      <TornPaperOverlay frame={frame} color="#0066ff" />
      <AbsoluteFill style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 20,
      }}>
        <h1 style={{
          fontFamily: 'Arial Black, sans-serif',
          fontSize: 80,
          color: 'white',
          opacity: upToOpacity,
          letterSpacing: 8,
        }}>
          UP TO
        </h1>
        <h1 style={{
          fontFamily: 'Arial Black, sans-serif',
          fontSize: 180,
          color: 'white',
          transform: `scale(${percentScale})`,
          opacity: percentOpacity,
          letterSpacing: 10,
          textShadow: '6px 6px 0px rgba(0, 0, 0, 0.5)',
        }}>
          30%
        </h1>
        <GlitchText frame={frame} delay={20} style={{ fontSize: 120, letterSpacing: 8 }}>
          OFF
        </GlitchText>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// 场景3: BEST SELLER
const Scene3: React.FC = () => {
  const frame = useCurrentFrame();

  const textScale = spring({
    frame: frame,
    from: 0.8,
    to: 1,
    fps: 30,
    config: { damping: 10, stiffness: 100 },
  });
  const textOpacity = interpolate(frame, [0, 5], [0, 1], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill style={{ backgroundColor: '#0a0a0a' }}>
      <div style={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gridTemplateRows: 'repeat(3, 1fr)',
        gap: 2,
      }}>
        {Array.from({ length: 9 }).map((_, i) => (
          <div key={i} style={{ position: 'relative', overflow: 'hidden' }}>
            <Img
              src={IMAGES.product1}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                filter: 'saturate(1.3) contrast(1.2)',
              }}
            />
          </div>
        ))}
      </div>
      <AbsoluteFill style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}>
        <h1 style={{
          fontFamily: 'Arial Black, sans-serif',
          fontSize: 100,
          fontWeight: 900,
          color: '#ffffff',
          textShadow: '6px 6px 0px rgba(0, 0, 0, 0.8), 0 0 30px rgba(255, 255, 255, 0.3)',
          transform: `scale(${textScale})`,
          opacity: textOpacity,
          letterSpacing: 8,
        }}>
          BEST SELLER
        </h1>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// 场景4: LAST CALL
const Scene4: React.FC = () => {
  const frame = useCurrentFrame();

  const textLines = 5;
  const pulseScale = spring({
    frame: frame,
    from: 0.9,
    to: 1.1,
    fps: 30,
    config: { damping: 5, stiffness: 50 },
  });

  return (
    <AbsoluteFill style={{ backgroundColor: 'black' }}>
      <AbsoluteFill style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 10,
      }}>
        {Array.from({ length: textLines }).map((_, i) => {
          const delay = i * 3;
          const opacity = interpolate(frame - delay, [0, 10], [0, 1], { extrapolateRight: 'clamp' });
          const translateX = interpolate(frame - delay, [0, 10], [i % 2 === 0 ? -50 : 50, 0], {
            extrapolateRight: 'clamp',
          });

          const isCenter = i === Math.floor(textLines / 2);

          return (
            <h1
              key={i}
              style={{
                fontFamily: 'Arial Black, sans-serif',
                fontSize: isCenter ? `${60 * pulseScale}` : 40,
                color: isCenter ? '#0066ff' : 'white',
                textAlign: 'center',
                transform: `translateX(${translateX}px)`,
                opacity,
                letterSpacing: 6,
              }}
            >
              LAST CALL
            </h1>
          );
        })}
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// 场景5: STYLISH ORGANIC
const Scene5: React.FC = () => {
  const frame = useCurrentFrame();

  return (
    <AbsoluteFill>
      <Img
        src={IMAGES.orchard}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          filter: 'brightness(0.8) saturate(1.2)',
        }}
      />
      <AbsoluteFill style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 20,
      }}>
        <OutlineText frame={frame} delay={0}>STYLISH</OutlineText>
        <AnimatedText frame={frame} delay={5}>ORGANIC</AnimatedText>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// 场景6: BUILT TO LAST / SHOP NOW
const Scene6: React.FC = () => {
  const frame = useCurrentFrame();

  return (
    <AbsoluteFill>
      <Img
        src={IMAGES.tree}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          filter: 'saturate(1.3)',
        }}
      />
      <AbsoluteFill style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 20,
      }}>
        <OutlineText frame={frame} delay={0} style={{ fontSize: 80 }}>BUILT TO LAST</OutlineText>
        <GlitchText frame={frame} delay={10} style={{ fontSize: 100, letterSpacing: 8 }}>SHOP NOW</GlitchText>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// 场景7: SHOP NOW (结尾)
const Scene7: React.FC = () => {
  const frame = useCurrentFrame();

  const shopNowScale = spring({
    frame: frame,
    from: 0.8,
    to: 1,
    fps: 30,
    config: { damping: 10, stiffness: 100 },
  });
  const shopNowOpacity = interpolate(frame, [0, 5], [0, 1], { extrapolateRight: 'clamp' });
  const urlOpacity = interpolate(frame, [10, 20], [0, 1], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill style={{ backgroundColor: 'black' }}>
      <TornPaperOverlay frame={frame} color="#0066ff" />
      <AbsoluteFill style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 30,
      }}>
        <h1 style={{
          fontFamily: 'Arial Black, sans-serif',
          fontSize: 160,
          fontWeight: 900,
          color: 'white',
          textAlign: 'center',
          transform: `scale(${shopNowScale})`,
          opacity: shopNowOpacity,
          letterSpacing: 10,
        }}>
          SHOP NOW
        </h1>
        <p style={{
          fontFamily: 'Arial, sans-serif',
          fontSize: 40,
          color: 'white',
          textAlign: 'center',
          opacity: urlOpacity,
          letterSpacing: 4,
        }}>
          www.brandname.com
        </p>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// 主组件
export const LuluEcommerceVideo: React.FC = () => {
  const { fps } = useVideoConfig();

  return (
    <AbsoluteFill>
      {/* Scene 1: 0-1秒 */}
      <Sequence from={0} durationInFrames={fps * 1}>
        <Scene1 />
      </Sequence>

      {/* Scene 2: 1-3秒 */}
      <Sequence from={fps * 1} durationInFrames={fps * 2}>
        <Scene2 />
      </Sequence>

      {/* Scene 3: 3-4秒 */}
      <Sequence from={fps * 3} durationInFrames={fps * 1}>
        <Scene3 />
      </Sequence>

      {/* Scene 4: 4-5秒 */}
      <Sequence from={fps * 4} durationInFrames={fps * 1}>
        <Scene4 />
      </Sequence>

      {/* Scene 5: 5-7秒 */}
      <Sequence from={fps * 5} durationInFrames={fps * 2}>
        <Scene5 />
      </Sequence>

      {/* Scene 6: 7-8秒 */}
      <Sequence from={fps * 7} durationInFrames={fps * 1}>
        <Scene6 />
      </Sequence>

      {/* Scene 7: 8-10秒 */}
      <Sequence from={fps * 8} durationInFrames={fps * 2}>
        <Scene7 />
      </Sequence>
    </AbsoluteFill>
  );
};
